import { useState, useRef, useEffect } from "react";
import { motion, useInView, useReducedMotion, animate } from "framer-motion";
import { Reveal, SectionLabel } from "./Reveal";
import { MoveHorizontal, TrendingUp, Gauge, Search, Star } from "lucide-react";

const CountUp = ({ to, decimals = 0, suffix = "", prefix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const reduced = useReducedMotion();
  useEffect(() => {
    if (!inView) return;
    const el = ref.current;
    if (reduced) {
      el.textContent = prefix + to.toFixed(decimals).replace(".", ",") + suffix;
      return;
    }
    const controls = animate(0, to, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => {
        el.textContent = prefix + v.toFixed(decimals).replace(".", ",") + suffix;
      },
    });
    return () => controls.stop();
  }, [inView, to, decimals, suffix, prefix, reduced]);
  return <span ref={ref}>0</span>;
};

const OldSite = () => (
  <div className="absolute inset-0 bg-[#d8d5cc] p-3 md:p-5" style={{ fontFamily: "Times New Roman, serif" }} aria-hidden="true">
    <div className="bg-[#efede6] border-2 border-[#9a968a] h-full p-3 md:p-5 overflow-hidden flex flex-col">
      <p className="text-center text-sm md:text-xl font-bold text-[#5a5648] underline">Willkommen auf unserer Homepage!!!</p>
      <p className="text-center text-[8px] md:text-[10px] text-[#7a7668] italic mt-0.5">--- Malerbetrieb Weber --- Ihr Partner rund um Farbe ---</p>
      <div className="mt-2 flex gap-1.5 flex-wrap justify-center">
        {["Home", "Über uns", "Leistungen", "Impressum", "Gästebuch", "Links"].map((x) => (
          <span key={x} className="text-[8px] md:text-[10px] bg-[#c4c0b2] border border-[#9a968a] px-1.5 md:px-2 py-0.5 text-[#5a5648]">{x}</span>
        ))}
      </div>
      <div className="mt-2 md:mt-3 h-12 md:h-20 bg-[#c4c0b2] border border-[#9a968a] flex items-center justify-center">
        <span className="text-[9px] md:text-[11px] text-[#7a7668] italic">[ Bild lädt nicht … ]</span>
      </div>
      <p className="mt-2 text-[9px] md:text-[11px] text-[#5a5648] leading-snug flex-1">
        Sehr geehrte Damen und Herren, herzlich willkommen auf unserer Internetseite. Wir sind ein
        Malerbetrieb. Klicken Sie hier für mehr Infos. Bei Fragen rufen Sie uns an oder senden Sie
        ein Fax. Zuletzt aktualisiert: 2014. Diese Seite ist für Internet Explorer 8 optimiert.
      </p>
      <div className="mt-2 flex items-center justify-between gap-2">
        <p className="text-[8px] md:text-[10px] text-[#8a2b2b]">⚠ Nicht für Mobilgeräte optimiert · Ladezeit: 6,4 s</p>
        <p className="text-[8px] md:text-[10px] text-[#7a7668]">Besucher: 003841</p>
      </div>
    </div>
  </div>
);

const NewSite = () => (
  <div className="absolute inset-0 bg-white p-3 md:p-5" aria-hidden="true">
    <div className="h-full rounded-xl border border-[#E4E4E7] shadow-sm overflow-hidden bg-white flex flex-col">
      <div className="flex items-center justify-between px-3 md:px-5 py-2 md:py-2.5 border-b border-[#F1F5F9]">
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-4 md:w-5 md:h-5 rounded bg-[#2563EB] flex items-center justify-center text-white font-display font-bold text-[7px] md:text-[9px]">W</span>
          <span className="font-display font-semibold text-[10px] md:text-sm">Malerbetrieb Weber</span>
        </span>
        <span className="hidden md:flex gap-4 text-[10px] text-[#52525B] font-medium">
          <span>Leistungen</span><span>Referenzen</span><span>Kontakt</span>
        </span>
        <span className="rounded-full bg-[#2563EB] text-white text-[7px] md:text-[10px] font-semibold px-2 md:px-3.5 py-1 md:py-1.5 shadow-sm">Angebot anfordern</span>
      </div>
      <div className="flex-1 grid grid-cols-[1.1fr_1fr] gap-2.5 md:gap-4 px-3 md:px-5 py-2.5 md:py-4 min-h-0">
        <div className="flex flex-col justify-center min-w-0">
          <span className="inline-flex self-start items-center gap-1 rounded-full bg-[#EFF6FF] text-[#2563EB] font-mono text-[6px] md:text-[8px] font-semibold px-1.5 md:px-2 py-0.5 md:py-1 uppercase tracking-wider">Meisterbetrieb seit 1998</span>
          <p className="mt-1.5 md:mt-2.5 font-display font-semibold text-[13px] md:text-2xl tracking-tight leading-[1.1]">
            Frische Wände<br />in 48 Stunden.<br />
            <span className="text-[#2563EB]">Festpreis-Garantie.</span>
          </p>
          <p className="mt-1 md:mt-2 text-[7px] md:text-[10px] text-[#52525B] leading-relaxed max-w-[95%]">
            Sauber, pünktlich, zum vereinbarten Preis — Innenanstrich, Fassade und Lackierung in Ratingen &amp; Umgebung.
          </p>
          <div className="mt-1.5 md:mt-3 flex items-center gap-1.5 md:gap-2">
            <span className="rounded-full bg-[#0A0A0A] text-white text-[7px] md:text-[9px] font-semibold px-2 md:px-3 py-1 md:py-1.5">Termin sichern</span>
            <span className="rounded-full border border-[#E4E4E7] text-[7px] md:text-[9px] font-semibold px-2 md:px-3 py-1 md:py-1.5">Referenzen</span>
          </div>
          <div className="mt-1.5 md:mt-3 flex items-center gap-1.5">
            <span className="flex text-amber-400 text-[8px] md:text-[11px]">★★★★★</span>
            <span className="text-[6px] md:text-[9px] text-[#52525B] font-medium">5,0 · 47 Google-Bewertungen</span>
          </div>
        </div>
        <div className="relative rounded-lg overflow-hidden border border-[#E4E4E7] min-h-0">
          <img
            src="/mockup/interior.jpg"
            alt=""
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <span className="absolute bottom-1.5 left-1.5 md:bottom-2.5 md:left-2.5 flex items-center gap-1 rounded-md bg-white/90 backdrop-blur-sm px-1.5 md:px-2.5 py-0.5 md:py-1.5 shadow-sm">
            <span className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-emerald-500" />
            <span className="text-[6px] md:text-[8px] font-semibold">Projekt: Altbau-Sanierung, fertig in 2 Tagen</span>
          </span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 md:gap-3 px-3 md:px-5 pb-2.5 md:pb-4">
        {[
          { label: "Innenanstrich", sub: "ab 12 €/m²", img: "/mockup/roller.jpg" },
          { label: "Fassade", sub: "inkl. Gerüst", img: "https://images.unsplash.com/photo-1628012209120-d9db7abf7eab?crop=entropy&cs=srgb&fm=jpg&q=60&w=400" },
          { label: "Lackierung", sub: "Türen & Heizkörper", img: "/mockup/lack.jpg" },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border border-[#E4E4E7] overflow-hidden bg-white">
            <div className="h-6 md:h-12 overflow-hidden">
              <img src={s.img} alt="" loading="lazy" className="w-full h-full object-cover" />
            </div>
            <div className="px-1.5 md:px-2.5 py-1 md:py-1.5">
              <p className="text-[7px] md:text-[9px] font-semibold leading-tight">{s.label}</p>
              <p className="text-[6px] md:text-[8px] text-[#A1A1AA] leading-tight">{s.sub}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const BeforeAfter = () => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const updateFromClientX = (clientX) => {
    const rect = containerRef.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, pct)));
  };

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      updateFromClientX(e.touches ? e.touches[0].clientX : e.clientX);
    };
    const up = () => (dragging.current = false);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      data-testid="before-after-slider"
      className="relative aspect-[16/11] md:aspect-[16/9] rounded-2xl overflow-hidden border border-[#E4E4E7] shadow-[0_24px_60px_-24px_rgba(10,10,10,0.2)] select-none cursor-ew-resize"
      onMouseDown={(e) => { dragging.current = true; updateFromClientX(e.clientX); }}
      onTouchStart={(e) => { dragging.current = true; updateFromClientX(e.touches[0].clientX); }}
    >
      <NewSite />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <OldSite />
      </div>
      <div className="absolute top-0 bottom-0 w-0.5 bg-[#2563EB]" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-[#2563EB] shadow-lg flex items-center justify-center">
          <MoveHorizontal className="w-5 h-5 text-white" aria-hidden="true" />
        </div>
      </div>
      <span className="absolute top-3 left-3 rounded-full bg-[#0A0A0A]/80 text-white font-mono text-[10px] px-3 py-1.5 backdrop-blur-sm">VORHER</span>
      <span className="absolute top-3 right-3 rounded-full bg-[#2563EB] text-white font-mono text-[10px] px-3 py-1.5">NACHHER</span>
      <input
        type="range"
        min="4"
        max="96"
        value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        aria-label="Vorher-Nachher-Vergleich verschieben"
        data-testid="before-after-range"
        className="absolute bottom-0 inset-x-0 opacity-0 h-8 cursor-ew-resize"
      />
    </div>
  );
};

const metrics = [
  { icon: TrendingUp, value: 180, suffix: " %", prefix: "+", label: "mehr Anfragen im Schnitt", sub: "nach Relaunch, erste 6 Monate" },
  { icon: Gauge, value: 0.8, decimals: 1, suffix: " s", label: "Ladezeit statt 6+ Sekunden", sub: "gemessen mit Google PageSpeed" },
  { icon: Search, value: 3, prefix: "Top ", label: "bei Google in deiner Region", sub: "lokale Suchbegriffe" },
  { icon: Star, value: 5.0, decimals: 1, label: "Kundenzufriedenheit", sub: "alle bisherigen Projekte" },
];

export default function Signature() {
  return (
    <section className="py-20 md:py-32">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <Reveal>
          <SectionLabel number="03" label="Der Unterschied" />
          <h2 className="font-display font-medium text-3xl md:text-4xl lg:text-5xl tracking-tight max-w-2xl">
            Sieh selbst, was ein Relaunch verändert.
          </h2>
          <p className="mt-5 text-base md:text-lg text-[#52525B] max-w-xl leading-relaxed">
            Ziehe den Regler und vergleiche: links eine typische veraltete Website, rechts das,
            was deine Kunden künftig sehen.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-12">
          <BeforeAfter />
        </Reveal>
        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="results-dashboard">
          {metrics.map((m, i) => (
            <Reveal key={m.label} delay={i * 0.1}>
              <div className="h-full rounded-2xl border border-[#E4E4E7] bg-white p-6 hover:border-[#2563EB]/40 transition-colors duration-300">
                <m.icon className="w-5 h-5 text-[#2563EB] mb-4" aria-hidden="true" />
                <p className="font-mono font-semibold text-2xl md:text-3xl tracking-tight">
                  <CountUp to={m.value} decimals={m.decimals || 0} suffix={m.suffix || ""} prefix={m.prefix || ""} />
                </p>
                <p className="mt-2 text-sm font-medium leading-snug">{m.label}</p>
                <p className="mt-1 text-xs text-[#A1A1AA]">{m.sub}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
